import { f7minPercentage } from '@/utils/settings';

export const scaleOptions = [
  'interpolate',
  ['linear'],
];

const getPropertyValue = propertyName => [
  'case',
  ['boolean', ['has', propertyName], true],
  ['get', propertyName],
  0,
];

export const defineComputedVar = (featuresName = [], operation = '+', expression = 'get') => {
  let computedVar = [];

  if (featuresName.length > 1) {
    computedVar = [operation];
    featuresName.forEach(featureName => {
      if (operation === '+') {
        computedVar.push(getPropertyValue(featureName));
      } else {
        computedVar.push([expression, featureName]);
      }
    });
  }
  return computedVar;
};

/**
 * Build a maplibre expression to divide
 * the values of activity data by the total data
 *
 * @param {array} activityFeaturesName
 * @param {array} featuresNameTotal
 * @returns {array : maplibre expression}
 */
const getActivityData = (
  activityFeaturesName = [], featuresNameTotal = [],
) => {
  if (activityFeaturesName.length === 0) {
    return [];
  }

  // one feature
  if (activityFeaturesName.length === 1) {
    return ['/', ['coalesce', ['get', activityFeaturesName[0]], 0], ['get', featuresNameTotal[0]]];
  }

  const activitiesSum = defineComputedVar(activityFeaturesName, '+', 'get');
  const totalSum = defineComputedVar(featuresNameTotal, '+', 'get');

  return ['/', activitiesSum, totalSum];
};

/**
 * Create a maplibre expression to compute the max
 * of several maplibre expressions
 *
 * @param {function} getActivityFeaturesName
 * @param {array} featuresNameTotal
 * @returns {array : maplibre expression}
 */
export const maxOfActivities = (getActivityFeaturesName, featuresNameTotal) => {
  const activitiesSum = ['cycling', 'skiing', 'walking', 'running'].map(activity => {
    const activityFeaturesName = getActivityFeaturesName(activity);
    return getActivityData(activityFeaturesName, featuresNameTotal);
  });

  return ['max', ...activitiesSum];
};

/**
 * Return a maplibre expression defining a computed variable
 * for the max of activities
 *
 * @param {function} getActivityFeaturesName
 * @param {array} featuresNameTotal
 * @returns {array : maplibre expression defining a computed variable}
 */
export const defineF7maxVar = (getActivityFeaturesName, featuresNameTotal) => {
  const maxVar = ['let', 'max'];

  const maxOfActivitiesData = maxOfActivities(getActivityFeaturesName, featuresNameTotal);

  return [...maxVar, maxOfActivitiesData];
};

/**
 * Create a maplibre expression to compute the max value
 * of the data and defined a color given the activity being the max
 * If none are above a specific percentage
 * color is #777777
 * @param {function} getActivityFeaturesName
 * @param {arrays} featuresNameTotal
 * @param {object} colors
 * @returns {array : maplibre expression}
 */
export const defineF7case = (getActivityFeaturesName, featuresNameTotal, colors, type = 'sum') => {
  const caseDefinition = [
    'case',
    ['<', ['var', 'max'], f7minPercentage],
    '#777777',
  ];

  ['cycling', 'skiing', 'walking', 'running'].forEach(activity => {
    const activityFeaturesName = getActivityFeaturesName(activity);
    const activityData = getActivityData(activityFeaturesName, featuresNameTotal, type);
    caseDefinition.push(['==', ['var', 'max'], activityData]);
    caseDefinition.push(colors[activity]);
  });

  caseDefinition.push('#000000');
  return caseDefinition;
};

export const getPaintProperty = (valueProperty, scaleOpts, colorOptions, isComputedVar = false) => {
  if (isComputedVar && valueProperty.length >= 3) {
    // computed properties are defined as ['let', 'density', [...]]
    const valueName = valueProperty[1];

    return [
      ...valueProperty,
      [
        ...scaleOpts,
        ['var', valueName],
        ...colorOptions,
      ],
    ];
  }
  if (isComputedVar && valueProperty.length >= 3) {
    return [];
  }

  return [
    ...scaleOpts,
    valueProperty,
    ...colorOptions,
  ];
};

/**
 * Create a mapbox expression to get the value from the featuresName
 * @param {array} featuresName
 * @param {string} type
 *  simple : get the first feature
 *  sum : sum the features array
 *  mean : mean the features array
 * @returns {array : mapbox expression}
 */
export const getValueProperty = (
  featuresName = [], type = 'simple', varName = null, includePropDeclaration = true,
  operation = null, featuresName2 = [],
) => {
  const hasSecondFeatureOperation = operation && featuresName2;

  if (featuresName.length >= 1 && type === 'simple') {
    const valueProp = !hasSecondFeatureOperation
      ? ['get', featuresName[0]]
      // custom operation with other features (used for F8)
      : [
        operation,
        ['get', featuresName[0]],
        ['get', featuresName2[0]],
      ];

    return valueProp;
  }

  const computedPropertySum = defineComputedVar(featuresName, '+', 'get');

  if (featuresName.length >= 1 && type === 'sum') {
    const name = varName || 'sum';

    if (!includePropDeclaration) {
      return computedPropertySum;
    }

    return [
      'let',
      name,
      computedPropertySum,
    ];
  }

  if (featuresName.length >= 1 && type === 'mean') {
    const name = varName || 'mean';

    if (!includePropDeclaration) {
      return ['/', computedPropertySum, featuresName.length];
    }

    return [
      'let',
      name,
      ['/', computedPropertySum, featuresName.length],
    ];
  }

  // custom operation with other features (used for F8)
  // if type is not 'simple'
  if (featuresName.length >= 1 && hasSecondFeatureOperation) {
    const name = varName || 'customOperation';
    const computedProperty2Sum = defineComputedVar(featuresName2, '+', 'get');

    return [
      'let',
      name,
      [operation, computedPropertySum, computedProperty2Sum],
    ];
  }

  return null;
};

/**
 * In the case of a computed variable
 * include its declaration ['var', varName] after it's computation
 * varName : valueProp[1] as it's defined as ['let', varName, []]
 * @param {array of strings} featuresName
 * @param {array : mapbox expression} valueProp
 * @returns {array : mapbox expression}
 */
export const getValuePropWithDeclaration = (featuresName = [], valueProp = ['let', 'varName', []]) => {
  const isComputedVar = featuresName.length > 1;
  return isComputedVar
    ? [...valueProp, ['var', valueProp[1]]]
    : valueProp;
};

export const getFilterProperty = featuresName => {
  let filterProperty = null;
  // one value
  if (featuresName.length === 1) {
    filterProperty = ['has', featuresName[0]];
  } else {
    // more than one
    filterProperty = defineComputedVar(featuresName, 'any', 'has');
  }

  return filterProperty;
};
